import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { IAppState } from 'spw-redux/reducer';
import * as GA from 'spw-shared/analytics';
import { Column, Row, SPWExportButton } from 'spw-shared/globalComponents';
import {
  DEFAULT_STREET_ADDRESS,
  getProgramCalculationTitle,
} from 'spw-constants';
import { IUser } from 'spw-redux/modules/user/types';
import { bindActionCreators, Dispatch } from 'redux';
import { pathOr } from 'ramda';
import { IBuilding } from 'spw-redux/modules/projects/types';
import {
  selectCurrentScenario,
  selectProgramSummaryCalculator,
} from 'spw-redux/modules/calculators/selectors';
import { fetchCurrentWorksheet as fetchCurrentWorksheetAction } from 'spw-redux/modules/calculators/actions';
import { isDepartmentScenario, isMacro } from 'spw-components/worksheet/utils';
import { ScenarioHeader } from 'spw-components/downloads/image-header';
import { getZipNamePrefix, sanitizeFileName } from 'spw-shared/utils';
import { JustifyEndRow } from 'spw-shared/globalComponents/grid';
import { ShowHideDecimalToggle } from 'spw-components/global/ShowHideDecimalToggle';
import { prepareAndExecuteReportDownload } from 'spw-redux/modules/downloads/actions';
import { REPORT_TYPES } from 'spw-redux/modules/reports/constants';
import { ReportAttributes, PNG } from 'spw-redux/modules/reports/types';
import USFBuildingCalculationsRightPanel from './USF-building-calculations-right-panel';
import USFBuildingCalculationsLeftPanel from './USF-building-calculations-left-panel';
import { ColumnFlex1, CustomPaper, USFBuildingTitle } from './styles';
import { PlainProgramScenario } from '../../../models/Scenario';
import { SummaryCalculator } from '../../../models/ProgramSummaryCalculator';

interface IUSFBuildingCalculations {
  scenario: PlainProgramScenario;
  programSummaryCalculator: SummaryCalculator;
  scenarios?: PlainProgramScenario[];
  building: IBuilding;
  user?: IUser;
  isEditingSavedScenario?: boolean;
  fetchCurrentWorksheet?: () => void;
  inBrowserDownload?: boolean;
  isDownloadingIndividualReport?: boolean;
  individualReportZipName?: null | string;
  initialShowDecimals?: boolean;
}

export const USFBuildingCalculations: React.FC<IUSFBuildingCalculations> = ({
  scenario,
  programSummaryCalculator,
  scenarios,
  user,
  building,
  isEditingSavedScenario,
  inBrowserDownload,
  isDownloadingIndividualReport,
  individualReportZipName,
  initialShowDecimals = false,
}) => {
  const [showDecimals, setShowDecimals] = useState(initialShowDecimals);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isEditingSavedScenario) {
      setShowDecimals(false);
    }
  }, [isEditingSavedScenario]);

  const toggleShowDecimals = React.useCallback(
    () => setShowDecimals((prev) => !prev),
    [setShowDecimals]
  );

  // TODO: Move to the backend
  const zipName = useMemo(() => {
    let prefix = 'Bldg';
    if (scenario?.floorId) {
      const floor = building.floors.find((f) => scenario.floorId === f.id);
      if (floor) {
        prefix = floor.name;
      }
    } else if (isMacro(scenario)) {
      prefix = 'Macro';
    } else if (isDepartmentScenario(scenario)) {
      const { department } = scenario;
      prefix = department ? department.name : 'Multiple Depts';
    }

    return `${
      building.streetAddress &&
      building.streetAddress !== DEFAULT_STREET_ADDRESS
        ? `${building?.streetAddress}_`
        : ''
    }${scenario.name}_`
      .concat(getZipNamePrefix(scenario, prefix))
      .concat('Summary Calcs.zip');
  }, [scenario, building]);

  const handleExport = useCallback(() => {
    GA.event({
      category: 'Programming',
      subCategory: GA.dupCategoryFromCalculator(scenario?.calculatorType),
      action: GA.tagFromExportCalculator(scenario?.calculatorType),
      label: JSON.stringify({
        projectType: 'programming',
        userId: user.id,
      }),
    });

    dispatch(
      prepareAndExecuteReportDownload(
        {
          reportType: REPORT_TYPES.SUMMARY_CALCULATIONS,
          entityId: scenario.id,
          format: PNG,
        } as ReportAttributes,
        zipName
      )
    );
  }, [scenario, zipName, user, dispatch]);

  const isDownload = window.SPWExport || inBrowserDownload;

  return (
    <CustomPaper>
      {isDownload && (
        <ScenarioHeader
          scenario={scenario}
          building={building}
          styles={{ marginBottom: 18 }}
        />
      )}
      <Row>
        <ColumnFlex1>
          <Row width="calc(94% + 16px)">
            <USFBuildingTitle data-testid="summary_calcs_title">
              <div>{getProgramCalculationTitle(scenario.calculatorType)}</div>
              <div>SUMMARY CALCULATIONS</div>
            </USFBuildingTitle>
            <div style={{ flex: 1 }} />
            <div
              style={{
                display: isDownload ? 'none' : 'flex',
              }}
            >
              {!isDownload && !isEditingSavedScenario && (
                <ShowHideDecimalToggle
                  showDecimals={showDecimals}
                  toggleShowDecimals={toggleShowDecimals}
                  labelPlacement="start"
                />
              )}
            </div>
          </Row>
          <USFBuildingCalculationsLeftPanel
            scenario={scenario}
            programSummaryCalculator={programSummaryCalculator}
            building={building}
            scenarios={scenarios}
            showDecimals={showDecimals}
            inBrowserDownload={inBrowserDownload}
          />
        </ColumnFlex1>
        <ColumnFlex1>
          <Row>
            <Column style={{ flex: 1 }} />
            <JustifyEndRow
              style={{
                display: isDownload ? 'none' : 'block',
                paddingRight: 3,
                height: '36px',
              }}
            >
              <SPWExportButton
                busy={
                  isDownloadingIndividualReport &&
                  sanitizeFileName(zipName) === individualReportZipName
                }
                onClick={
                  isDownloadingIndividualReport ? undefined : handleExport
                }
                disabled={isEditingSavedScenario}
                testingPrefix="programming_building_details_macro_scenario_summary_calcs"
              />
            </JustifyEndRow>
          </Row>
          <USFBuildingCalculationsRightPanel
            calculatorType={scenario.calculatorType}
            isDownload={isDownload}
          />
        </ColumnFlex1>
      </Row>
    </CustomPaper>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      fetchCurrentWorksheet: fetchCurrentWorksheetAction,
    },
    dispatch
  );

const mapStateToProps = (state: IAppState) => ({
  scenario: selectCurrentScenario(state),
  programSummaryCalculator: selectProgramSummaryCalculator(state),
  scenarios: state.calculatorReducer.scenarios,
  user: state.userReducer.user,
  building: pathOr(
    null,
    ['projectsReducer', 'currentProject', 'buildings', 0],
    state
  ),
  isEditingSavedScenario: state.calculatorReducer.isEditingSavedScenario,
  individualReportZipName: state.downloadsReducer.zipName,
  isDownloadingIndividualReport:
    state.downloadsReducer.isDownloadingIndividualReport,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(USFBuildingCalculations);
